import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDraftMode } from "../../../contexts/DraftModeContext";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { type CallOffShiftsResult, useCallOffShifts } from "../../../hooks/useCallOffShift";
import { type RemovalDetails, useHandleNurseChange } from "../../../hooks/useHandleNurseChange";
import { trackEvent } from "../../../utils/analytics";
import { ChangesSource } from "../../../utils/models";
import { describeRemovalOutcome, type RemovalEntry, type RemovalPlan, toCallOffParams, toRemoveNursesEntry } from "./removeStaffSubmit";
import { REMOVE_STAFF_ANALYTICS_SOURCE, SINGLE_REMOVE_COUNT } from "./removeStaffUtils";

type Args = {
	weekIndex: number;
	dayNo: number;
	staffingDate: DateTime;
	/** The panel's caller, as the long-standing single-remove events report it. */
	source: string;
};

/** How one removal ended: which picks came off the schedule and which are still on it (with the first error). */
export type RemovalResult = { removed: RemovalEntry[]; failed: RemovalEntry[]; error: unknown };

/** The picks the call-off batch reported as failed, back as entries. */
const failedCallOffs = (entries: readonly RemovalEntry[], result: CallOffShiftsResult): RemovalEntry[] => {
	const failedIds = new Set(result.failed.map((failure) => failure.params.shiftId));
	return entries.filter((entry) => failedIds.has(entry.row.shiftId));
};

/** The one publish for every pick that isn't called off, then this device's copy of the schedule. */
const usePublishRemovals = ({ weekIndex, dayNo, source }: Pick<Args, "weekIndex" | "dayNo" | "source">) => {
	const { removeNurseFromSpecificDate } = useHospitalData();
	const { isDraftMode } = useDraftMode();
	const { removeNurses } = useHandleNurseChange();
	return useCallback(
		async (entries: RemovalEntry[], details: RemovalDetails): Promise<RemovalResult> => {
			if (entries.length === 0) return { removed: [], failed: [], error: undefined };
			for (const { ref } of entries) trackEvent("Delete nurse clicked", { nurse_id: ref.shift.nurseId, shift_id: ref.shift.id });
			try {
				// The list is the date's scheduled shifts, so the removal is a scheduled one (the local copy below agrees).
				await removeNurses({
					entries: entries.map((entry) => toRemoveNursesEntry(entry, weekIndex, dayNo)),
					isActual: false,
					source: ChangesSource.Staffing,
					...details,
				});
			} catch (error) {
				return { removed: [], failed: entries, error };
			}
			for (const { ref } of entries) {
				// Each shift leaves its OWN bucket; draft mode marks it pending instead (auto-tracking picks that up).
				removeNurseFromSpecificDate(weekIndex, dayNo, ref.shiftIndex, ref.shift.nurseId, false, ref.shift.id, isDraftMode);
				trackEvent("Approve remove dialog", { nurse_id: ref.shift.nurseId, shift_id: ref.shift.id, source });
			}
			return { removed: entries, failed: [], error: undefined };
		},
		[removeNurses, removeNurseFromSpecificDate, weekIndex, dayNo, isDraftMode, source],
	);
};

/** Sequential call-offs (each starts its own replacement search); the batch refreshes the page once. */
const useCallOffRemovals = () => {
	const { mutateAsync: callOffShifts } = useCallOffShifts();
	return useCallback(
		async (entries: RemovalEntry[], details: RemovalDetails): Promise<RemovalResult> => {
			if (entries.length === 0) return { removed: [], failed: [], error: undefined };
			try {
				const result = await callOffShifts(entries.map((entry) => toCallOffParams(entry, details)));
				const failed = failedCallOffs(entries, result);
				return { removed: entries.filter((entry) => !failed.includes(entry)), failed, error: result.failed[0]?.error };
			} catch (error) {
				// The batch itself broke (the hook has shown why); nothing can be counted as called off.
				return { removed: [], failed: entries, error };
			}
		},
		[callOffShifts],
	);
};

/** The ONE snackbar a removal ends with. Draft mode saves nothing yet, so it only speaks when something failed. */
const useReportRemoval = (staffingDate: DateTime) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar } = useHospitalData();
	const { isDraftMode } = useDraftMode();
	return useCallback(
		(result: RemovalResult, searchedCount: number) => {
			const outcome = describeRemovalOutcome({ removed: result.removed, failed: result.failed, searchedCount, staffingDate, t });
			if (outcome.severity === "success") {
				if (!isDraftMode) showSnackbar(outcome.message, "success");
				return;
			}
			// One failed shift: pass the server's reason through, which names the problem better than a generic line.
			const error = result.failed.length === SINGLE_REMOVE_COUNT ? result.error : undefined;
			showErrorSnackbar(outcome.message, { shift_ids: result.failed.map((entry) => entry.row.shiftId).join(",") }, error);
		},
		[t, showSnackbar, showErrorSnackbar, isDraftMode, staffingDate],
	);
};

/**
 * Carries out a planned removal: the one publish first, then the call-offs, then one snackbar. A failure in
 * either leaves the rest done; the caller keeps the failed picks selected. Several picks are tracked as
 * `remove_staff_multi_remove_{start,success,error}`.
 */
export const useRemovalExecution = (args: Args) => {
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const { chosenDepartment } = useHospitalData();
	const publish = usePublishRemovals(args);
	const callOff = useCallOffRemovals();
	const report = useReportRemoval(args.staffingDate);

	return useCallback(
		async (plan: RemovalPlan, details: RemovalDetails, inWindowCount: number): Promise<RemovalResult> => {
			const selectedCount = plan.publish.length + plan.callOff.length;
			const isMulti = selectedCount > SINGLE_REMOVE_COUNT;
			const props = {
				source: REMOVE_STAFF_ANALYTICS_SOURCE,
				department_id: chosenDepartment.department_id,
				selected_count: selectedCount,
				in_window_count: inWindowCount,
			};
			if (isMulti) track("remove_staff_multi_remove_start", { ...props, searched_count: plan.callOff.length });
			const published = await publish(plan.publish, details);
			const calledOff = await callOff(plan.callOff, details);
			const result: RemovalResult = {
				removed: [...published.removed, ...calledOff.removed],
				failed: [...published.failed, ...calledOff.failed],
				// DEFAULT: the publish's error when it failed, else the first call-off's — the snackbar passes on one.
				error: published.error ?? calledOff.error,
			};
			report(result, calledOff.removed.length);
			if (result.failed.length > 0 && result.removed.length > 0) {
				// Some shifts came off: re-read the date's list so the panel shows who is really still on.
				void queryClient.invalidateQueries({ queryKey: ["nurses_to_remove", chosenDepartment.department_id] });
			}
			if (!isMulti) return result;
			if (result.failed.length === 0) track("remove_staff_multi_remove_success", { ...props, removed_count: result.removed.length });
			else trackError("remove_staff_multi_remove_error", { ...props, removed_count: result.removed.length, failed_count: result.failed.length });
			return result;
		},
		[chosenDepartment.department_id, publish, callOff, report, queryClient, track, trackError],
	);
};
